/**
 * ARVEXIS'S VOICE — full-screen voice selection (Phase 6).
 *
 * The roster, the audition endpoint, the preference endpoint and the capability rules are
 * unchanged product truth. What changed is the surface: one horizontal swipeable carousel of
 * the ten member-selectable voices instead of a stacked list.
 *
 * Swiping changes the focused candidate only. The member's saved voice changes exclusively when
 * they press "Use this voice". Nothing autoplays.
 *
 * The orb is the Founder-accepted canonical presence system (ArvexisPresence) — no second orb
 * implementation, no Phase 5.2 tuning reopened.
 */
import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Play, Pause, Check, Loader2 } from 'lucide-react';
import ArvexisPresence from './ArvexisPresence';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ArvexisVoiceAudition = ({ onClose, lang = 'en' }) => {
  const es = lang === 'es';
  const [data, setData] = useState(null);
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [loadingClip, setLoadingClip] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);
  const cacheRef = useRef({});
  const touchRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const [energy, setEnergy] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/voice/candidates`, { credentials: 'include' });
        if (!res.ok) throw new Error('unavailable');
        const body = await res.json();
        if (!alive) return;
        setData(body);
        const start = body.candidates?.findIndex((c) => c.id === (body.selected || body.in_use));
        setIndex(start > 0 ? start : 0);
      } catch (e) {
        if (alive) setError(es ? 'No se pudieron cargar las voces.' : 'Could not load the voices.');
      }
    })();
    return () => {
      alive = false;
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    };
  }, [es]);

  const stop = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    analyserRef.current = null;
    setEnergy(0);
    setPlaying(false);
  };

  // Truthful audible energy: measured from the sample that is actually playing.
  const attachAnalyser = (audio) => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const source = ctx.createMediaElementSource(audio);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      analyser.connect(ctx.destination);
      analyserRef.current = analyser;
      const buf = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteFrequencyData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) sum += buf[i];
        setEnergy(Math.min(1, (sum / buf.length) / 90));
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch (e) {
      // no analyser available: the orb stays in calm Presence rather than faking activity
    }
  };

  const candidates = data?.candidates || [];
  const current = candidates[index];
  const sampleId = data?.samples?.find((s) => (es ? /es/i.test(s.id) : true))?.id
    || data?.samples?.[0]?.id;
  const isSelected = current && (data?.selected === current.id);
  const supportsTalk = current ? current.support?.talk !== false : true;

  const move = (delta) => {
    if (!candidates.length) return;
    stop();
    setError(null);
    setIndex((i) => (i + delta + candidates.length) % candidates.length);
  };

  const listen = async () => {
    if (!current || !sampleId) return;
    const key = `${current.id}:${sampleId}`;
    if (playing) { stop(); return; }
    setError(null);
    try {
      if (!cacheRef.current[key]) {
        setLoadingClip(true);
        const res = await fetch(`${API_URL}/api/voice/audition`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ voice: current.id, sample: sampleId }),
        });
        if (!res.ok) throw new Error('synthesis failed');
        const body = await res.json();
        cacheRef.current[key] = `data:audio/mp3;base64,${body.audio_base64}`;
      }
      const audio = new Audio(cacheRef.current[key]);
      audio.onended = () => stop();
      audioRef.current = audio;
      setPlaying(true);
      attachAnalyser(audio);
      await audio.play();
    } catch (e) {
      setError(es ? 'Esa voz no pudo hablar ahora.' : "That voice couldn't speak just now.");
      setPlaying(false);
    } finally {
      setLoadingClip(false);
    }
  };

  const use = async () => {
    if (!current || isSelected) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/voice/preference`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ voice: current.id }),
      });
      if (!res.ok) throw new Error('save failed');
      setData((d) => ({ ...d, selected: current.id, in_use: current.id, awaiting_selection: false }));
    } catch (e) {
      setError(es ? 'No se pudo guardar la selección.' : "Couldn't save that selection.");
    } finally {
      setSaving(false);
    }
  };

  // One item = one slide: the focused voice physically travels out, its neighbour travels in.
  const [slide, setSlide] = useState(() =>
    Math.min(Math.round((typeof window === 'undefined' ? 390 : window.innerWidth) * 0.66), 300));
  useEffect(() => {
    const onResize = () => setSlide(Math.min(Math.round(window.innerWidth * 0.66), 300));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const pad = typeof window === 'undefined' ? 0 : Math.max((window.innerWidth - slide) / 2, 0);

  const onDragEnd = (_e, info) => {
    const dx = info.offset.x;
    const fast = Math.abs(info.velocity.x) > 320;
    if (dx < -slide * 0.28 || (fast && dx < -24)) move(1);
    else if (dx > slide * 0.28 || (fast && dx > 24)) move(-1);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="voice-surface"
      data-testid="voice-selection-surface"
    >
      <div className="voice-surface__head">
        <button
          onClick={() => { stop(); onClose(); }}
          className="voice-back"
          aria-label={es ? 'Volver' : 'Back'}
          data-testid="voice-back-btn"
        >
          <ChevronLeft size={18} />
        </button>
        <span className="voice-surface__wordmark">ARVEXIS</span>
        <h1 className="voice-surface__title" data-testid="voice-title">
          {es ? 'La voz de Arvexis' : "Arvexis's Voice"}
        </h1>
        <p className="voice-surface__sub">
          {es ? 'Encuentra la voz que se sienta bien.' : 'Find the voice that feels right.'}
        </p>
      </div>

      {!data && !error && (
        <div className="voice-stage" data-testid="voice-loading">
          <Loader2 className="animate-spin" size={22} style={{ color: 'rgba(244,239,231,0.5)' }} />
        </div>
      )}

      {current && (
        <>
          <div className="voice-track-viewport" data-testid="voice-stage">
            <button className="voice-arrow voice-arrow--left" onClick={() => move(-1)}
                    aria-label={es ? 'Voz anterior' : 'Previous voice'} data-testid="voice-prev-btn">
              <ChevronLeft size={18} />
            </button>
            <motion.div
              className="voice-track"
              data-testid="voice-track"
              drag="x"
              dragElastic={0.12}
              dragMomentum={false}
              onDragEnd={onDragEnd}
              animate={{ x: -index * slide }}
              transition={{ type: 'spring', stiffness: 260, damping: 32 }}
              style={{ paddingLeft: pad, paddingRight: pad }}
            >
              {candidates.map((c, i) => {
                const active = i === index;
                return (
                  <div
                    key={c.id}
                    className={`voice-item${active ? ' voice-item--active' : ''}`}
                    style={{ width: slide }}
                    data-testid={`voice-item-${c.id}`}
                    data-active={active ? 'true' : 'false'}
                  >
                    <div
                      className="voice-item__orb"
                      {...(active ? { 'data-testid': 'voice-focused-orb', 'data-audible': playing ? 'true' : 'false' } : {})}
                    >
                      <ArvexisPresence
                        size="medium"
                        profile="audition"
                        state={active && playing ? 'speaking' : 'idle'}
                        audioVolume={active && playing ? energy : 0}
                      />
                    </div>
                    <h2 className="voice-meta__name" {...(active ? { 'data-testid': 'voice-name' } : {})}>
                      {c.label}
                    </h2>
                    <p className="voice-meta__note" {...(active ? { 'data-testid': 'voice-note' } : {})}>
                      {c.note}
                    </p>
                  </div>
                );
              })}
            </motion.div>
            <button className="voice-arrow voice-arrow--right" onClick={() => move(1)}
                    aria-label={es ? 'Voz siguiente' : 'Next voice'} data-testid="voice-next-btn">
              <ChevronRight size={18} />
            </button>
          </div>

          <div className="voice-actions">
            <div className="voice-dots" data-testid="voice-dots">
              {candidates.map((c, i) => (
                <span key={c.id} className={`voice-dot${i === index ? ' voice-dot--on' : ''}`} />
              ))}
            </div>

            {!supportsTalk && (
              <p className="voice-unsupported" data-testid="voice-unsupported-note">
                {es ? 'Esta voz no está disponible para Talk.' : 'This voice is not available for Talk.'}
              </p>
            )}

            <button className="voice-btn-listen" onClick={listen} disabled={loadingClip}
                    data-testid="voice-listen-btn">
              {loadingClip ? <Loader2 size={18} className="animate-spin" />
                : playing ? <Pause size={18} /> : <Play size={18} />}
              {loadingClip
                ? (es ? 'Preparando…' : 'Preparing…')
                : playing
                  ? (es ? 'Detener' : 'Stop')
                  : (es ? 'Toca para escuchar' : 'Tap to listen')}
            </button>

            <button
              className={`voice-btn-use${isSelected ? ' voice-btn-use--selected' : ''}`}
              onClick={use}
              disabled={saving || isSelected || !supportsTalk}
              data-testid="voice-use-btn"
            >
              {saving ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
              {isSelected
                ? (es ? 'Voz seleccionada' : 'Selected voice')
                : (es ? 'Usar esta voz' : 'Use this voice')}
            </button>

            {/* 6D — the member is told which voice they are hearing before they ever open the
                carousel, and whether it is their own choice or the one Arvexis fell back to.
                Straight from the server's own resolution; this surface names no voice itself. */}
            {data.awaiting_selection && (
              <p style={{ fontSize: '12.5px', textAlign: 'center', lineHeight: 1.5,
                          color: 'rgba(232,197,143,0.72)', margin: 0 }}
                 data-testid="voice-provisional-note">
                {es
                  ? `Arvexis habla con ${(data.candidates || []).find((c) => c.id === data.in_use)?.label || data.in_use} hasta que elijas una voz.`
                  : `Arvexis is speaking with ${(data.candidates || []).find((c) => c.id === data.in_use)?.label || data.in_use} until you choose a voice.`}
              </p>
            )}

            <p style={{ fontSize: '12px', textAlign: 'center', color: 'rgba(244,239,231,0.34)', margin: 0 }}
               data-testid="voice-parity-note">
              {es ? 'Una voz para Talk, Message y Dreams.' : 'One voice for Talk, Message and Dreams.'}
            </p>

            {error && (
              <p style={{ fontSize: '13px', textAlign: 'center', color: 'rgba(255,190,190,0.9)', margin: 0 }}
                 data-testid="voice-error">{error}</p>
            )}
          </div>
        </>
      )}

      {error && !data && (
        <div className="voice-actions">
          <p style={{ fontSize: '14px', textAlign: 'center', color: 'rgba(255,190,190,0.9)' }}
             data-testid="voice-load-error">{error}</p>
        </div>
      )}
    </motion.div>
  );
};

export default ArvexisVoiceAudition;
